/**
 * One tooltip for the whole cabinet, driven by `data-tip` on any control.
 *
 * Delegated rather than per-widget: a panel that wants an explanation writes
 * one attribute and forgets about it. Hover, keyboard focus and long-press all
 * open it — the third of those because a phone has no hover, and the shop hides
 * what a tower actually does behind exactly that gesture.
 *
 * Optional attributes:
 *   data-tip       "Title — body copy"
 *   data-tip-icon  an id from the vendored set, shown beside the title
 *   data-tip-cost  a number, rendered as a cash row
 */

import { el, icon, installUiTheme, isIconName, money } from './Icon';
import type { IconName } from './Icon';

export interface TooltipState {
  /** Suppressed while a modal is open or a drag is in flight. */
  enabled?: boolean;
}

export interface TooltipContent {
  title?: string;
  body: string;
  icon?: IconName;
  rows?: readonly (readonly [string, string])[];
}

const OPEN_DELAY_MS = 220;
/** Held this long without moving, a touch is a request for an explanation. */
const LONG_PRESS_MS = 420;
/** Past this the finger is dragging a tower, not asking about one. */
const LONG_PRESS_SLOP_PX = 9;
/**
 * Clearance from the host's edges.
 *
 * 16 rather than 8 because the tube is not the visible boundary: the bezel is
 * a 6px border plus an 8px inset shadow drawn over the glass, so a bubble
 * clamped to 8px is half-buried under moulded plastic. Matches `--bezel-safe`.
 */
const EDGE_PAD = 16;
/** Clearance between the tooltip and the control it describes. */
const ANCHOR_GAP = 6;

export class Tooltip {
  private root = el('div', 'tooltip');
  private head = el('div', 'tooltip__title');
  private headText = el('span');
  private body = el('div', 'tooltip__body');
  private rows = el('dl', 'tooltip__rows');
  private headIcon: HTMLElement | null = null;
  private host: HTMLElement | null = null;
  private anchor: HTMLElement | null = null;
  private timer = 0;
  private enabled = true;
  private mounted = false;
  private pressPointer = -1;
  private pressX = 0;
  private pressY = 0;

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();
    this.host = root;

    this.head.append(this.headText);
    this.root.append(this.head, this.body, this.rows);
    this.root.hidden = true;
    this.root.setAttribute('role', 'tooltip');
    root.append(this.root);

    root.addEventListener('pointerover', this.onOver, true);
    root.addEventListener('pointerout', this.onOut, true);
    root.addEventListener('focusin', this.onOver, true);
    root.addEventListener('focusout', this.onOut, true);
    root.addEventListener('pointerdown', this.onDown, true);
    // A touch drag has to be able to cancel a pending long-press, and it ends
    // wherever the finger lifts — which is often off the control it started on.
    window.addEventListener('pointermove', this.onMove, true);
    window.addEventListener('pointerup', this.onUp, true);
    window.addEventListener('pointercancel', this.onUp, true);
    window.addEventListener('keydown', this.onKey);
  }

  update(state: TooltipState): void {
    if (state.enabled !== undefined) {
      this.enabled = state.enabled;
      if (!this.enabled) this.hide();
    }
  }

  /** Opens the tooltip against an arbitrary element, bypassing `data-tip`. */
  showFor(anchor: HTMLElement, content: TooltipContent): void {
    if (!this.mounted || !this.enabled) return;
    this.anchor = anchor;
    this.render(content);
    this.root.hidden = false;
    this.place(anchor);
  }

  hide(): void {
    window.clearTimeout(this.timer);
    this.timer = 0;
    this.anchor = null;
    this.pressPointer = -1;
    this.root.hidden = true;
  }

  destroy(): void {
    const root = this.host;
    if (root) {
      root.removeEventListener('pointerover', this.onOver, true);
      root.removeEventListener('pointerout', this.onOut, true);
      root.removeEventListener('focusin', this.onOver, true);
      root.removeEventListener('focusout', this.onOut, true);
      root.removeEventListener('pointerdown', this.onDown, true);
    }
    window.removeEventListener('pointermove', this.onMove, true);
    window.removeEventListener('pointerup', this.onUp, true);
    window.removeEventListener('pointercancel', this.onUp, true);
    window.removeEventListener('keydown', this.onKey);
    window.clearTimeout(this.timer);
    this.root.remove();
    this.host = null;
    this.mounted = false;
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  private render(content: TooltipContent): void {
    if (this.headIcon) { this.headIcon.remove(); this.headIcon = null; }
    if (content.icon) {
      this.headIcon = icon(content.icon, { size: 12, role: 'uiAccent', glow: 0.6 });
      this.head.prepend(this.headIcon);
    }
    this.headText.textContent = content.title ?? '';
    this.head.hidden = !content.title && !content.icon;
    this.body.textContent = content.body;

    this.rows.replaceChildren();
    const rows = content.rows ?? [];
    this.rows.hidden = rows.length === 0;
    for (const [label, value] of rows) {
      this.rows.append(el('dt', undefined, label), el('dd', 'num', value));
    }
  }

  /**
   * Places the bubble inside the host, never over the anchor, never clipped.
   *
   * The host is #hud, which fills the tube — and the tube clips. So the bubble
   * is clamped to the *host* rect rather than to the window: a viewport-space
   * clamp still lets the last shop tile's tooltip run past the tube's right
   * edge, where `overflow: hidden` cuts it mid-word. That was the bug.
   *
   * Four candidates in order, first fit wins: above, below, left, right. A tile
   * in the right-hand rail cannot take a centred bubble without overhanging the
   * cabinet, so it flips to the left side instead of being shoved back on top
   * of the thing it is describing. If nothing fits — a tooltip taller than the
   * tube on a 390px phone — the clamp still keeps every edge inside the host,
   * which is the one guarantee that must not fail.
   */
  private place(anchor: HTMLElement): void {
    const host = this.host;
    if (!host) return;
    const a = anchor.getBoundingClientRect();
    const t = this.root.getBoundingClientRect();
    const h = host.getBoundingClientRect();

    const minX = h.left + EDGE_PAD;
    const maxX = h.right - EDGE_PAD - t.width;
    const minY = h.top + EDGE_PAD;
    const maxY = h.bottom - EDGE_PAD - t.height;

    const centredX = clamp(a.left + a.width / 2 - t.width / 2, minX, maxX);
    const centredY = clamp(a.top + a.height / 2 - t.height / 2, minY, maxY);

    const above = a.top - ANCHOR_GAP - t.height;
    const below = a.bottom + ANCHOR_GAP;
    const leftOf = a.left - ANCHOR_GAP - t.width;
    const rightOf = a.right + ANCHOR_GAP;

    let x = centredX;
    let y: number;
    if (above >= minY) {
      y = above;
    } else if (below <= maxY) {
      y = below;
    } else if (leftOf >= minX) {
      x = leftOf;
      y = centredY;
    } else if (rightOf <= maxX) {
      x = rightOf;
      y = centredY;
    } else {
      y = above >= minY ? above : below;
    }

    // maxX can fall below minX when the bubble is wider than the host; the
    // clamp order then pins it to the left edge, which is still fully readable.
    this.root.style.left = `${Math.round(clamp(x, minX, Math.max(minX, maxX)) - h.left)}px`;
    this.root.style.top = `${Math.round(clamp(y, minY, Math.max(minY, maxY)) - h.top)}px`;
  }

  private contentFrom(node: HTMLElement): TooltipContent | null {
    const tip = node.dataset.tip;
    if (!tip) return null;
    const [head, ...rest] = tip.split(' — ');
    const content: TooltipContent = rest.length > 0
      ? { title: head, body: rest.join(' — ') }
      : { body: head };
    const iconId = node.dataset.tipIcon;
    if (iconId && isIconName(iconId)) content.icon = iconId;
    const cost = node.dataset.tipCost;
    if (cost) content.rows = [['Cost', money(Number(cost))]];
    return content;
  }

  private readonly onOver = (ev: Event): void => {
    if (!this.enabled) return;
    // A tap synthesises pointerover. Opening on it would flash the bubble under
    // the finger on every shop tap; touch gets the long-press path instead.
    if (ev instanceof PointerEvent && ev.pointerType !== 'mouse') return;
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const node = target.closest<HTMLElement>('[data-tip]');
    if (!node || node === this.anchor) return;
    const content = this.contentFrom(node);
    if (!content) return;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.showFor(node, content), OPEN_DELAY_MS);
  };

  private readonly onOut = (ev: Event): void => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    if (this.anchor && target.closest('[data-tip]') !== this.anchor) return;
    this.hide();
  };

  /**
   * Mouse: a click dismisses. Touch: the press *is* the request, so it arms a
   * long-press instead. The shop's own drag threshold is 5px and this one is
   * 9px, so a drag-to-place always wins the gesture.
   */
  private readonly onDown = (ev: Event): void => {
    if (!(ev instanceof PointerEvent) || ev.pointerType === 'mouse') {
      this.hide();
      return;
    }
    this.hide();
    if (!this.enabled) return;
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const node = target.closest<HTMLElement>('[data-tip]');
    if (!node) return;
    const content = this.contentFrom(node);
    if (!content) return;
    this.pressPointer = ev.pointerId;
    this.pressX = ev.clientX;
    this.pressY = ev.clientY;
    this.timer = window.setTimeout(() => this.showFor(node, content), LONG_PRESS_MS);
  };

  private readonly onMove = (ev: PointerEvent): void => {
    if (this.pressPointer < 0 || ev.pointerId !== this.pressPointer) return;
    const dx = ev.clientX - this.pressX;
    const dy = ev.clientY - this.pressY;
    if (Math.hypot(dx, dy) < LONG_PRESS_SLOP_PX) return;
    this.hide();
  };

  /** The bubble survives the lift, so what it says can actually be read. */
  private readonly onUp = (ev: PointerEvent): void => {
    if (this.pressPointer < 0 || ev.pointerId !== this.pressPointer) return;
    this.pressPointer = -1;
    if (this.root.hidden) window.clearTimeout(this.timer);
  };

  private readonly onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') this.hide();
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
